import { apiFetchJSON } from '../../core/api.js';
import { escapeHtmlText } from '../../core/format.js';
import { getInput } from '../../core/dom.js';

export type ComponentFieldCategory =
  | 'cpu'
  | 'gpu'
  | 'motherboard'
  | 'ram'
  | 'storage'
  | 'psu'
  | 'case'
  | 'cooler';

type FieldMode = 'strict' | 'spec-or-catalog' | 'optional';

const FIELD_CATEGORIES: Record<string, ComponentFieldCategory> = {
  'comp-cpu': 'cpu',
  'comp-gpu': 'gpu',
  'comp-motherboard': 'motherboard',
  'comp-ram': 'ram',
  'comp-ssd': 'storage',
  'comp-psu': 'psu',
  'comp-case': 'case',
  'comp-cooling': 'cooler'
};

const FIELD_MODE: Record<string, FieldMode> = {
  'comp-cpu': 'strict',
  'comp-gpu': 'strict',
  'comp-ram': 'spec-or-catalog',
  'comp-ssd': 'spec-or-catalog',
  'comp-motherboard': 'optional',
  'comp-psu': 'optional',
  'comp-case': 'optional',
  'comp-cooling': 'optional'
};

const DELEGATE_IDS: Record<string, string> = {
  'comp-cpu': 'delegate:cpu',
  'comp-gpu': 'delegate:gpu',
  'comp-motherboard': 'delegate:motherboard',
  'comp-ram': 'delegate:ram',
  'comp-ssd': 'delegate:storage',
  'comp-psu': 'delegate:psu',
  'comp-case': 'delegate:case',
  'comp-cooling': 'delegate:cooler'
};

const DELEGATE_INFO_TEXT =
  'Отметьте «Подберём сами», если точную модель пока не выбрали. Мы подберём оптимальный вариант под вашу сборку и укажем его в разделе «Мои заказы» после оформления. Конфигурацию можно согласовать до оплаты.';

interface SuggestItem {
  id: string;
  name: string;
  kind?: 'spec' | 'catalog';
}

interface SuggestResponse {
  ok?: boolean;
  items?: SuggestItem[];
  error?: string;
}

const selections = new Map<string, { id: string; name: string }>();
const fieldMeta = new Map<string, { category: ComponentFieldCategory; withDelegate: boolean }>();
const boundSuggestIds = new Set<string>();
let bound = false;
let globalHandlersBound = false;
let activeFieldId: string | null = null;
let debounceTimer = 0;
let fetchSeq = 0;
let delegateInfoSheetOpen: (() => void) | null = null;
let suppressSuggestInputId: string | null = null;

const PRESET_CREATE_SPEC_FIELDS: Record<string, ComponentFieldCategory> = {
  'create-step-cpu': 'cpu',
  'create-step-gpu': 'gpu',
  'create-step-motherboard': 'motherboard',
  'create-step-ram': 'ram',
  'create-step-psu': 'psu',
  'create-step-storage': 'storage',
  'create-step-case': 'case'
};

const PRESET_EDIT_SPEC_CLASSES: Record<string, ComponentFieldCategory> = {
  'preset-edit-cpu': 'cpu',
  'preset-edit-gpu': 'gpu',
  'preset-edit-motherboard': 'motherboard',
  'preset-edit-ram': 'ram',
  'preset-edit-psu': 'psu',
  'preset-edit-storage': 'storage',
  'preset-edit-case': 'case'
};

function initDelegateInfoSheet(): void {
  const sheet = document.getElementById('comp-delegate-info-sheet');
  const backdrop = document.getElementById('comp-delegate-info-backdrop');
  const okBtn = document.getElementById('comp-delegate-info-ok');
  if (!sheet) return;
  const sheetEl = sheet;
  const textEl = document.getElementById('comp-delegate-info-text');
  if (textEl) textEl.textContent = DELEGATE_INFO_TEXT;
  let closeTimer = 0;

  function close(): void {
    if (closeTimer) {
      window.clearTimeout(closeTimer);
      closeTimer = 0;
    }
    sheetEl.classList.remove('sheet-open');
    sheetEl.classList.add('sheet-closing');
    closeTimer = window.setTimeout(() => {
      sheetEl.classList.remove('sheet-closing');
      sheetEl.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('sheet-raise-app');
      closeTimer = 0;
    }, 320);
  }

  okBtn?.addEventListener('click', close);
  backdrop?.addEventListener('click', close);

  delegateInfoSheetOpen = function open(): void {
    if (closeTimer) {
      window.clearTimeout(closeTimer);
      closeTimer = 0;
    }
    sheetEl.classList.remove('sheet-closing');
    sheetEl.classList.add('sheet-open');
    sheetEl.setAttribute('aria-hidden', 'false');
    document.body.classList.add('sheet-raise-app');
  };
}

function debounce(ms: number, fn: () => void): void {
  window.clearTimeout(debounceTimer);
  debounceTimer = window.setTimeout(fn, ms);
}

function isSpecId(id: string): boolean {
  return id.startsWith('spec:');
}

function isDelegateId(id: string): boolean {
  return id.startsWith('delegate:');
}

export function getComponentSelection(inputId: string): { id: string; name: string } | null {
  return selections.get(inputId) || null;
}

function clearFieldError(inputId: string): void {
  const input = getInput(inputId);
  const errEl = document.getElementById(inputId + '-error');
  input?.classList.remove('form-input-error');
  if (errEl) errEl.textContent = '';
}

function markFieldError(inputId: string): { ok: false; message: string } {
  const input = getInput(inputId);
  const errEl = document.getElementById(inputId + '-error');
  input?.classList.add('form-input-error');
  if (errEl) errEl.textContent = '';
  return { ok: false, message: '' };
}

function getPlaceholderText(inputId: string): string {
  const input = getInput(inputId);
  return (input?.placeholder || '').trim();
}

function applyDelegateUi(inputId: string, active: boolean, displayValue?: string): void {
  const input = getInput(inputId);
  if (!input) return;
  const wrap = input.closest('.comp-field');
  const checkbox = document.querySelector<HTMLInputElement>(
    '.comp-delegate-check-input[data-for="' + inputId + '"]'
  );

  if (active) {
    const label = displayValue || getPlaceholderText(inputId);
    input.value = label;
    input.disabled = true;
    input.readOnly = true;
    input.classList.add('comp-input-delegate', 'comp-input-delegate-disabled');
    wrap?.classList.add('comp-field--delegate');
    if (checkbox) checkbox.checked = true;
  } else {
    input.disabled = false;
    input.readOnly = false;
    input.classList.remove('comp-input-delegate', 'comp-input-delegate-disabled');
    wrap?.classList.remove('comp-field--delegate');
    if (checkbox) checkbox.checked = false;
  }
}

function setSelection(inputId: string, item: SuggestItem | null): void {
  const input = getInput(inputId);
  if (!input) return;
  if (item) {
    selections.set(inputId, { id: item.id, name: item.name });
    if (isDelegateId(item.id)) {
      applyDelegateUi(inputId, true, item.name || getPlaceholderText(inputId));
    } else {
      input.value = item.name;
      input.dataset.partId = item.id;
      input.classList.add('comp-input-selected');
      applyDelegateUi(inputId, false);
    }
    clearFieldError(inputId);
  } else {
    selections.delete(inputId);
    delete input.dataset.partId;
    input.classList.remove('comp-input-selected');
    applyDelegateUi(inputId, false);
  }
}

function setDelegateChecked(inputId: string, checked: boolean): void {
  const input = getInput(inputId);
  if (!input) return;
  if (checked) {
    closeAllLists();
    const ph = getPlaceholderText(inputId);
    setSelection(inputId, {
      id: DELEGATE_IDS[inputId] || 'delegate:unknown',
      name: ph
    });
    return;
  }
  setSelection(inputId, null);
  input.value = '';
}

function clearSelectionIfEdited(inputId: string): void {
  const input = getInput(inputId);
  const sel = selections.get(inputId);
  if (!input || !sel || input.disabled) return;
  if (isDelegateId(sel.id)) return;
  if (input.value.trim() !== sel.name) {
    setSelection(inputId, null);
    input.classList.remove('comp-input-delegate');
    input.closest('.comp-field')?.classList.remove('comp-field--delegate');
  }
}

function closeAllLists(): void {
  document.querySelectorAll('.comp-suggest-list').forEach((el) => {
    (el as HTMLElement).hidden = true;
    el.innerHTML = '';
  });
  activeFieldId = null;
}

function getListForInput(inputId: string): HTMLUListElement | null {
  const input = getInput(inputId);
  if (!input) return null;
  const wrap = input.closest('.comp-autocomplete');
  return wrap ? wrap.querySelector('.comp-suggest-list') : null;
}

function renderList(inputId: string, items: SuggestItem[]): void {
  const list = getListForInput(inputId);
  const input = getInput(inputId);
  if (!list || !input) return;

  if (!items.length) {
    list.innerHTML = '<li class="comp-suggest-empty">Ничего не найдено</li>';
    list.hidden = false;
    activeFieldId = inputId;
    return;
  }

  list.innerHTML = items
    .map((item, index) => {
      const isSpec = isSpecId(item.id);
      const sub = isSpec ? '<span class="comp-suggest-item-sub">модель на согласовании</span>' : '';
      return (
        '<li><button type="button" class="comp-suggest-item' +
        (isSpec ? ' comp-suggest-item--spec' : '') +
        '" role="option" data-index="' +
        index +
        '" data-id="' +
        escapeHtmlText(item.id) +
        '" data-name="' +
        escapeHtmlText(item.name) +
        '"><span class="comp-suggest-item-lines"><span class="comp-suggest-item-title">' +
        escapeHtmlText(item.name) +
        '</span>' +
        sub +
        '</span></button></li>'
      );
    })
    .join('');
  list.hidden = false;
  activeFieldId = inputId;
  input.setAttribute('aria-expanded', 'true');
}

function getFieldCategory(inputId: string): ComponentFieldCategory | null {
  const meta = fieldMeta.get(inputId);
  if (meta) return meta.category;
  return FIELD_CATEGORIES[inputId] || null;
}

async function fetchSuggestions(inputId: string, query: string): Promise<void> {
  const category = getFieldCategory(inputId);
  if (!category) return;
  const seq = ++fetchSeq;
  const url =
    '/api/components/suggest?category=' +
    encodeURIComponent(category) +
    '&q=' +
    encodeURIComponent(query) +
    '&limit=8';

  try {
    const data = await apiFetchJSON<SuggestResponse>(url);
    if (seq !== fetchSeq) return;
    if (!data.ok) {
      renderList(inputId, []);
      return;
    }
    renderList(inputId, Array.isArray(data.items) ? data.items : []);
  } catch {
    if (seq !== fetchSeq) return;
    const list = getListForInput(inputId);
    if (list) {
      list.innerHTML = '<li class="comp-suggest-empty">Не удалось загрузить подсказки</li>';
      list.hidden = false;
    }
  }
}

function pickItem(inputId: string, id: string, name: string): void {
  fetchSeq += 1;
  suppressSuggestInputId = inputId;
  const meta = fieldMeta.get(inputId);
  if (meta?.withDelegate) {
    setSelection(inputId, { id, name });
  } else {
    const input = getInput(inputId);
    if (input) input.value = name;
  }
  closeAllLists();
  const input = getInput(inputId);
  input?.setAttribute('aria-expanded', 'false');
  input?.focus();
  queueMicrotask(() => {
    if (suppressSuggestInputId === inputId) suppressSuggestInputId = null;
  });
}

function onInput(inputId: string): void {
  if (suppressSuggestInputId === inputId) return;
  const input = getInput(inputId);
  const withDelegate = fieldMeta.get(inputId)?.withDelegate;
  if (!input || input.disabled || input.classList.contains('comp-input-delegate')) return;
  if (withDelegate) clearSelectionIfEdited(inputId);
  const q = input.value.trim();
  if (q.length < 2) {
    closeAllLists();
    return;
  }
  debounce(250, () => {
    void fetchSuggestions(inputId, q);
  });
}

function ensureSuggestGlobalHandlers(): void {
  if (globalHandlersBound) return;
  globalHandlersBound = true;
  document.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement;
    if (
      target.closest('.comp-autocomplete') ||
      target.closest('.comp-delegate-row') ||
      target.closest('#comp-delegate-info-sheet')
    ) {
      return;
    }
    closeAllLists();
    document.querySelectorAll<HTMLInputElement>('.comp-autocomplete .form-input').forEach((inp) => {
      inp.setAttribute('aria-expanded', 'false');
    });
  });
}

function bindCatalogSuggest(
  inputId: string,
  category: ComponentFieldCategory,
  options: { withDelegate?: boolean } = {}
): void {
  if (boundSuggestIds.has(inputId)) return;
  const input = getInput(inputId);
  if (!input || input.closest('.comp-autocomplete')) return;

  const withDelegate = options.withDelegate === true;
  boundSuggestIds.add(inputId);
  fieldMeta.set(inputId, { category, withDelegate });

  const parent = input.parentNode;
  if (!parent) return;

  const label =
    input.previousElementSibling instanceof HTMLElement &&
    input.previousElementSibling.classList.contains('form-label')
      ? input.previousElementSibling
      : null;
  const errorEl = document.getElementById(inputId + '-error');

  const group = document.createElement('div');
  group.className = 'comp-field-group';
  group.dataset.compGroup = inputId;
  parent.insertBefore(group, label || input);
  if (label) group.appendChild(label);

  const fieldWrap = document.createElement('div');
  fieldWrap.className = 'comp-field';
  fieldWrap.dataset.compField = inputId;

  const autoWrap = document.createElement('div');
  autoWrap.className = 'comp-autocomplete';
  group.appendChild(fieldWrap);
  fieldWrap.appendChild(autoWrap);
  autoWrap.appendChild(input);

  input.setAttribute('autocomplete', 'off');
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-expanded', 'false');

  const list = document.createElement('ul');
  list.className = 'comp-suggest-list';
  list.setAttribute('role', 'listbox');
  list.hidden = true;
  autoWrap.appendChild(list);

  if (withDelegate) {
    const delegateRow = document.createElement('div');
    delegateRow.className = 'comp-delegate-row';

    const checkLabel = document.createElement('label');
    checkLabel.className = 'comp-delegate-check';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'comp-delegate-check-input';
    checkbox.dataset.for = inputId;

    const checkBox = document.createElement('span');
    checkBox.className = 'comp-delegate-check-box';
    checkBox.setAttribute('aria-hidden', 'true');

    const checkText = document.createElement('span');
    checkText.className = 'comp-delegate-check-text';
    checkText.textContent = 'Подберём сами';

    checkLabel.appendChild(checkbox);
    checkLabel.appendChild(checkBox);
    checkLabel.appendChild(checkText);

    const infoBtn = document.createElement('button');
    infoBtn.type = 'button';
    infoBtn.className = 'order-info-btn comp-delegate-info-btn';
    infoBtn.setAttribute('aria-label', 'Как работает подбор');
    infoBtn.textContent = 'i';

    delegateRow.appendChild(checkLabel);
    delegateRow.appendChild(infoBtn);
    fieldWrap.appendChild(delegateRow);
    if (errorEl) group.appendChild(errorEl);

    checkbox.addEventListener('change', () => {
      setDelegateChecked(inputId, checkbox.checked);
    });
    infoBtn.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      delegateInfoSheetOpen?.();
    });
  }

  input.addEventListener('input', () => onInput(inputId));
  input.addEventListener('focus', () => {
    if (input.disabled) return;
    const q = input.value.trim();
    if (q.length < 2) return;
    if (withDelegate && selections.has(inputId)) return;
    void fetchSuggestions(inputId, q);
  });
  input.addEventListener('keydown', (ev) => {
    if (activeFieldId !== inputId) return;
    const buttons = list.querySelectorAll<HTMLButtonElement>('.comp-suggest-item');
    if (!buttons.length) return;
    const current = list.querySelector<HTMLButtonElement>('.comp-suggest-item.is-active');
    let idx = current ? Number(current.dataset.index) : -1;

    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      idx = Math.min(idx + 1, buttons.length - 1);
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      idx = Math.max(idx - 1, 0);
    } else if (ev.key === 'Enter' && idx >= 0) {
      ev.preventDefault();
      const btn = buttons[idx];
      pickItem(inputId, btn.dataset.id || '', btn.dataset.name || '');
      return;
    } else if (ev.key === 'Escape') {
      closeAllLists();
      input.setAttribute('aria-expanded', 'false');
      return;
    } else {
      return;
    }

    buttons.forEach((btn, i) => btn.classList.toggle('is-active', i === idx));
    buttons[idx]?.scrollIntoView({ block: 'nearest' });
  });

  list.addEventListener('mousedown', (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>('.comp-suggest-item');
    if (!btn) return;
    ev.preventDefault();
    pickItem(inputId, btn.dataset.id || '', btn.dataset.name || '');
  });
}

function bindField(inputId: string): void {
  const category = FIELD_CATEGORIES[inputId];
  if (!category) return;
  bindCatalogSuggest(inputId, category, { withDelegate: true });
}

/** Autocomplete for owner preset create wizard (step 2). */
export function initPresetSpecAutocomplete(): void {
  ensureSuggestGlobalHandlers();
  Object.entries(PRESET_CREATE_SPEC_FIELDS).forEach(([inputId, category]) => {
    bindCatalogSuggest(inputId, category, { withDelegate: false });
  });
}

/** Autocomplete for dynamically rendered owner preset edit cards. */
export function bindPresetEditSpecAutocomplete(root: ParentNode = document): void {
  ensureSuggestGlobalHandlers();
  root.querySelectorAll('.preset-edit-card').forEach((card) => {
    const presetId = card.getAttribute('data-preset-id');
    if (!presetId) return;
    Object.entries(PRESET_EDIT_SPEC_CLASSES).forEach(([className, category]) => {
      const input = card.querySelector('.' + className) as HTMLInputElement | null;
      if (!input) return;
      if (!input.id) input.id = className + '-' + presetId;
      bindCatalogSuggest(input.id, category, { withDelegate: false });
    });
  });
}

export function initComponentsAutocomplete(): void {
  if (bound) return;
  bound = true;

  initDelegateInfoSheet();
  ensureSuggestGlobalHandlers();
  Object.keys(FIELD_CATEGORIES).forEach(bindField);
}

export function formatComponentForOrder(inputId: string, label: string): string {
  const input = getInput(inputId);
  const val = (input?.value || '').trim();
  const sel = selections.get(inputId);

  if (!val) {
    return label + ': —';
  }
  if (sel && isDelegateId(sel.id)) return label + ': подберём';
  if (sel && isSpecId(sel.id)) return label + ': ' + val + ' (модель на согласовании)';
  return label + ': ' + val;
}

export function validateComponentSelection(
  inputId: string,
  _label: string
): { ok: true } | { ok: false; message: string } {
  const mode = FIELD_MODE[inputId] || 'strict';
  const input = getInput(inputId);
  const value = (input?.value || '').trim();
  const sel = selections.get(inputId);

  if (sel && isDelegateId(sel.id)) {
    clearFieldError(inputId);
    return { ok: true };
  }

  if (!value) {
    return markFieldError(inputId);
  }

  if (mode === 'optional') {
    if (!sel || sel.name !== value) {
      return markFieldError(inputId);
    }
    clearFieldError(inputId);
    return { ok: true };
  }

  if (!sel || sel.name !== value || !sel.id) {
    return markFieldError(inputId);
  }

  if (mode === 'strict' && isSpecId(sel.id)) {
    return markFieldError(inputId);
  }

  clearFieldError(inputId);
  return { ok: true };
}
